import { useMemo, useState, useRef, useEffect } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { SortingFn } from '@tanstack/react-table';
import {
    useGetKeywordsByRetailersQuery,
    useGetKeywordsQuery,
    useLazyExportKeywordsByRetailersQuery,
    useUpdateKeywordRetailersMutation,
} from 'core/API/keywordsApi';
import { IkeywordItem, IkeywordsByType, IUserInfo, TFormattedKeywordsByRetailer, TRetailerRow } from 'types';
import { caseInsensitiveStringsOrder } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { getCheckedKeywordsIds, getSelectedRetailers } from 'pages/Keywords/helpers/dataGenerators';
import { updateRetailerKeywordsByType } from 'pages/Keywords/helpers/handlers';
import { EXCEL_FILE_TYPE } from 'shared/const';
import FileSaver from 'file-saver';
import { useGetRetailerByClientQuery } from 'core/API/authApi';

const useKeywordsByRetailer = ({ readonly, userInfo }: { readonly: boolean; userInfo?: IUserInfo }) => {
    const { data: keywords } = useGetKeywordsQuery({ type: 'search' });
    const { data: keywordsByRetailers, isLoading, isError } = useGetKeywordsByRetailersQuery({});
    const [nestedKeywordsByRetailers, setNestedKeywordsByRetailers] = useState<TFormattedKeywordsByRetailer[]>([]);
    const [modalType, setModalType] = useState<'edit' | 'view'>('edit');
    const [exportKeywordsByRetailers, { isLoading: isExportLoading, isFetching }] =
        useLazyExportKeywordsByRetailersQuery();
    const [filters, setFilters] = useState<{ id: string; value: string }[]>([]);
    const [keywordFilter, setKeywordFilter] = useState<string>('');
    const [languages, setLanguages] = useState<string[]>([]);
    const toast = useToast();
    const [updateKeywordRetailers, { isLoading: isUpdateRetailersLoading }] = useUpdateKeywordRetailersMutation();
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const translate = useTranslation();
    const updateInitialRef = useRef(null);
    const [keywordsToSelect, setKeywordsToSelect] = useState({
        priorityKeyword: true,
        nonPriorityKeyword: true,
    });

    const [keywordsByType, setKeywordsByType] = useState<IkeywordsByType[]>([]);
    const [filteredKeywords, setFilteredKeywords] = useState<IkeywordsByType[]>([]);
    const [visibleKeywords, setVisibleKeywords] = useState<number[]>([]);
    const [treeOpenByDefault, setTreeOpenByDefault] = useState(false);

    const handleExportKeywordsByRetailer = async (withWeights = false) => {
        try {
            const { data: res, isError: isExportError } = await exportKeywordsByRetailers({
                isTemplate: false,
                withWeights,
            });
            if (isExportError || !res) throw new Error(translate('keyword.export.error') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });
            const fileName = withWeights
                ? (translate('keywords_by_retailers.export.file_name_weights') as string)
                : (translate('keywords_by_retailers.export.file_name') as string);
            FileSaver.saveAs(blob, fileName);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        const formattedKeywords: IkeywordsByType[] = [];
        if (keywords?.keywords) {
            keywords.keywords.forEach((keyword) => {
                const keywordType = keyword.keyword_type ? keyword.keyword_type : 'default';

                const index = formattedKeywords.findIndex((formattedKeyword) => formattedKeyword.type === keywordType);

                const keywordItem: IkeywordItem = {
                    // UAM expects company_keyword ids here, not global keyword ids
                    id: String(keyword.id),
                    name: keyword.name,
                    type: keywordType,
                    master: keyword.master,
                    checked: false,
                    language: keyword.language,
                    index: 0,
                };

                if (index === -1) {
                    formattedKeywords.push({
                        id: keywordType,
                        type: keywordType,
                        name: keywordType,
                        checked: false,
                        keywords: [
                            {
                                ...keywordItem,
                                index: 0,
                            },
                        ],
                    });
                } else {
                    formattedKeywords[index].keywords.push({
                        ...keywordItem,
                        index: formattedKeywords[index].keywords.length,
                    });
                }
            });
        }
        setKeywordsByType(formattedKeywords);
    }, [keywords]);

    useEffect(() => {
        setFilteredKeywords(
            keywordsByType.map((type) => {
                return {
                    ...type,
                    keywords: type.keywords.filter((keyword) => {
                        if (keywordsToSelect.priorityKeyword && keywordsToSelect.nonPriorityKeyword) {
                            return keyword.name.toLowerCase().includes(keywordFilter);
                        }
                        if (keywordsToSelect.priorityKeyword) {
                            return keyword.master === 1 && keyword.name.toLowerCase().includes(keywordFilter);
                        }
                        if (keywordsToSelect.nonPriorityKeyword) {
                            return keyword.master === 0 && keyword.name.toLowerCase().includes(keywordFilter);
                        }
                        return true;
                    }),
                };
            }),
        );
    }, [keywordsByType, keywordFilter, keywordsToSelect]);

    useEffect(() => {
        if (!isLoading && !isError && keywordsByRetailers) {
            const formattedKeywordsByRetailer: TFormattedKeywordsByRetailer[] = [];
            keywordsByRetailers.retailers.forEach((retailer) => {
                const subRowToAdd: TRetailerRow = {
                    id: retailer.id,
                    name: retailer.name,
                    level: 'retailer',
                    keywords: retailer.company_keywords,
                    parentId: retailer.type_id,
                };
                const retailerTypeIndex = formattedKeywordsByRetailer.findIndex((r) => r.id === retailer.type_id);
                if (retailerTypeIndex === -1) {
                    formattedKeywordsByRetailer.push({
                        id: retailer.type_id,
                        name: retailer.type_name,
                        level: 'type',
                        subRows: [subRowToAdd],
                    });
                } else {
                    if (!formattedKeywordsByRetailer[retailerTypeIndex].subRows) {
                        formattedKeywordsByRetailer[retailerTypeIndex].subRows = [];
                    }
                    formattedKeywordsByRetailer[retailerTypeIndex].subRows.push(subRowToAdd);
                }
            });
            setNestedKeywordsByRetailers(formattedKeywordsByRetailer);
        }
    }, [isLoading, isError, keywordsByRetailers]);

    const caseInsensitiveSort = useMemo(() => caseInsensitiveStringsOrder, []);
    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            caseInsensitive: caseInsensitiveSort,
        }),
        [],
    );

    const handleEdit = () => {
        setKeywordFilter('');
        setModalType('edit');
        updateRetailerKeywordsByType(nestedKeywordsByRetailers, setKeywordsByType);
        onOpen();
    };

    const handleViewKeywords = (keywordList: number[]) => {
        setKeywordFilter('');
        setVisibleKeywords(keywordList);
        setModalType('view');
        onOpen();
    };

    const defaultColumn = useMemo(
        () => ({
            meta: { Filter: DefaultColumnFilter },
        }),
        [],
    );

    const handleCloseAddAndUpdateModal = () => {
        onClose();
    };

    const handleSubmit = async () => {
        const retailerIds = getSelectedRetailers(nestedKeywordsByRetailers).map((e) => e.id);

        try {
            const resp = await updateKeywordRetailers({
                retailerIds,
                companyKeywordsIds: getCheckedKeywordsIds(keywordsByType),
                fields: ['id', 'company_keywords'],
            });
            if ('error' in resp) {
                throw new Error();
            } else {
                toast({
                    title: translate('keywords_by_retailer.update.success'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }

        handleCloseAddAndUpdateModal();
    };

    const props = useMemo(
        () => ({
            handleCloseAddAndUpdateModal,
            handleEdit,
            handleViewKeywords,
            setModalType,
            keywords,
            modalType,
            filters,
            setFilters,
            keywordFilter,
            setKeywordFilter,
            toast,
            isOpen,
            onOpen,
            onClose,
            treeOpenByDefault,
            setTreeOpenByDefault,
            translate,
            updateInitialRef,
            nestedKeywordsByRetailers,
            updateKeywordRetailers,
            isUpdateRetailersLoading,
            isLoading,
            setNestedKeywordsByRetailers,
            keywordsToSelect,
            setKeywordsToSelect,
            keywordsByType,
            setKeywordsByType,
            filteredKeywords,
            setFilteredKeywords,
            visibleKeywords,
            setVisibleKeywords,
            handleSubmit,
            defaultColumn,
            sortTypes,
            handleExportKeywordsByRetailer,
            isExportLoading,
            isFetching,
            languages,
            readonly,
        }),
        [
            keywords,
            filters,
            keywordFilter,
            isOpen,
            treeOpenByDefault,
            nestedKeywordsByRetailers,
            isUpdateRetailersLoading,
            isLoading,
            keywordsToSelect,
            keywordsByType,
            filteredKeywords,
            visibleKeywords,
            defaultColumn,
            sortTypes,
            isExportLoading,
            isFetching,
            languages,
            readonly,
        ],
    );

    return props;
};

export type TKeywordsByRetailers = ReturnType<typeof useKeywordsByRetailer>;

export default useKeywordsByRetailer;
